import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '../ui/card'
import { Input } from '../ui/input'
import { Button } from '../ui/button'
import { AlertCircle, Loader2, ChevronDown, Trash2, Plus } from 'lucide-react'
import RichTextEditor from 'components/RichTextEditor'
import useToast from 'hooks/useToast'
import useFileUploader from 'hooks/useFileUploader'
import { FileUpload } from './file-upload'
import { LogoPreview } from './logo-preview'
import type { DealFormData, UrlItem } from '../../lib/types'
import {
  buildTimelines,
  buildV1DepositMetadata,
  fetchDealById,
} from '../../lib/deal-api'
import {
  URL_TYPE_OPTIONS,
  V1_TVL_DEAL_TYPE,
  isV1ContractDealType,
} from '../../lib/constants'
import { isValidDealSlug, validateV1Deposit } from '../../lib/step-validators'
import {
  V1DepositFields,
  getV1DepositLockedKeys,
  isV1MultichainDeal,
  pickV1DepositFields,
  type V1DepositFieldKey,
  type V1DepositListPatch,
} from './steps/v1-deposit-fields'
import { cn } from '../../lib/utils'
import { getUrlErrorMessage } from '../../lib/validators'

interface V1SimplifiedEditProps {
  formData: DealFormData
  dealId: string
  onSubmit: (payload: any) => Promise<void>
  isLoading?: boolean
  /**
   * Render and save the v1 sale-contract section (deposit window, claim,
   * timeline). Only enable this where `onSubmit` persists `metadata.v1` and
   * `metadata.timelines`; the overview-only summary page must pass false.
   */
  includeSaleContract?: boolean
}

export function V1SimplifiedEdit({
  formData,
  dealId,
  onSubmit,
  isLoading = false,
  includeSaleContract = true,
}: V1SimplifiedEditProps) {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const { uploadFile } = useFileUploader()
  const [projectName, setProjectName] = useState(formData.projectName || '')
  const [slug, setSlug] = useState(formData.slug || '')
  // Blank keeps the stored slug, so only a different, non-empty value counts.
  const slugChanged = Boolean(slug.trim()) && slug.trim() !== formData.slug
  const [description, setDescription] = useState(formData.description || '')
  const [salesDetails, setSalesDetails] = useState(formData.salesDetails || '')
  const [logo, setLogo] = useState<File | null>(null)
  const [logoUrl, setLogoUrl] = useState<string>('')
  const [logoUploading, setLogoUploading] = useState(false)
  const [logoUploadError, setLogoUploadError] = useState<string | undefined>()
  const [banner, setBanner] = useState<File | null>(null)
  const [bannerUrl, setBannerUrl] = useState<string>('')
  const [bannerUploading, setBannerUploading] = useState(false)
  const [bannerUploadError, setBannerUploadError] = useState<
    string | undefined
  >()
  const [bannerDimensionError, setBannerDimensionError] = useState<
    string | undefined
  >()

  const [errors, setErrors] = useState<{
    projectName?: string
    description?: string
    salesDetails?: string
    [key: string]: string | undefined
  }>({})
  const [urls, setUrls] = useState<UrlItem[]>(
    (formData.urls || []).length
      ? formData.urls
      : [
          { id: 'website', type: 'website', url: formData.website || '' },
          { id: 'x', type: 'x', url: formData.twitter || '' },
        ],
  )
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isV1Deposit =
    includeSaleContract && isV1ContractDealType(formData.salesType)
  const isTvl = formData.salesType === V1_TVL_DEAL_TYPE
  const addressesEditable = isV1MultichainDeal(formData)
  // Chains already stored on the deal; their identifiers cannot be edited.
  const lockedConfigCount = (formData.v1DepositConfigs ?? []).length
  const [v1Fields, setV1Fields] = useState(() => pickV1DepositFields(formData))
  const updateV1Field = (field: V1DepositFieldKey, value: string) => {
    setV1Fields((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
  }
  const updateV1Lists = (patch: V1DepositListPatch) => {
    setV1Fields((prev) => ({ ...prev, ...patch }))
    // Row errors are keyed by index, which shifts when a row is removed.
    setErrors((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(
          ([key]) => !Object.keys(patch).some((list) => key.startsWith(list)),
        ),
      ),
    )
  }

  useEffect(() => {
    setProjectName(formData.projectName || '')
    setSlug(formData.slug || '')
    setDescription(formData.description || '')
    setSalesDetails(formData.salesDetails || '')
    setLogo(formData.logo || null)
    setLogoUrl((formData as any).logoUrl || '')
    setBanner((formData as any).banner || null)
    setBannerUrl(((formData as any).bannerUrl || '') as string)
    setV1Fields(pickV1DepositFields(formData))
    setUrls(
      (formData.urls || []).length
        ? formData.urls
        : [
            { id: 'website', type: 'website', url: formData.website || '' },
            { id: 'x', type: 'x', url: formData.twitter || '' },
          ],
    )
  }, [formData])

  const validate = (): boolean => {
    const newErrors: {
      slug?: string
      projectName?: string
      description?: string
      salesDetails?: string
    } = {}

    // Nothing is required when editing an existing deal; only values that
    // were filled in are checked.
    if (slugChanged && !isValidDealSlug(slug)) {
      newErrors.slug =
        'Use lowercase letters, numbers and single hyphens, e.g. my-deal-2'
    }
    if (isV1Deposit) {
      Object.assign(
        newErrors,
        validateV1Deposit(v1Fields, {
          tvl: isTvl,
          edit: true,
          skipFields: getV1DepositLockedKeys(addressesEditable),
          lockedConfigCount,
          addressesEditable,
        }).errors,
      )
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleLogoSelected = (file: File | null) => {
    setLogo(file)
    if (!file) {
      setLogoUploading(false)
      setLogoUploadError(undefined)
      setLogoUrl('')
      return
    }
    setLogoUploading(true)
    setLogoUploadError(undefined)
    uploadFile(
      { file, path: `submission/logo-${Date.now()}-${file.name}` },
      (res: { url: string }) => {
        setLogoUrl(res.url)
        setLogoUploading(false)
        setLogoUploadError(undefined)
      },
      () => {
        setLogoUploading(false)
        setLogoUploadError('Upload failed. Please try again.')
      },
    )
  }

  const handleBannerSelected = async (file: File | null) => {
    setBanner(file)
    if (!file) {
      setBannerUploading(false)
      setBannerUploadError(undefined)
      setBannerDimensionError(undefined)
      setBannerUrl('')
      return
    }

    setBannerUploading(true)
    setBannerUploadError(undefined)
    setBannerDimensionError(undefined)

    uploadFile(
      { file, path: `submission/banner-${Date.now()}-${file.name}` },
      (res: { url: string }) => {
        setBannerUrl(res.url)
        setBannerUploading(false)
        setBannerUploadError(undefined)
      },
      () => {
        setBannerUploading(false)
        setBannerUploadError('Upload failed. Please try again.')
      },
    )
  }

  const handleSubmit = async () => {
    if (!validate()) {
      return
    }

    setIsSubmitting(true)

    try {
      const rawDeal = await fetchDealById(dealId)

      const existingMetadata = rawDeal.metadata || {}
      const existingOverview = existingMetadata.overview || {}
      const existingProjectSummary = existingOverview.projectSummary || {}

      const buildUrlsPayload = (items: UrlItem[]) => {
        const website = items.find((u) => u.type === 'website')?.url || ''
        const x = items.find((u) => u.type === 'x')?.url || ''
        const discord = items.find((u) => u.type === 'discord')?.url || ''
        const other = items
          .filter(
            (u) =>
              !['website', 'x', 'discord'].includes(u.type) && u.url.trim(),
          )
          .map((u) => ({ url: u.url.trim(), type: u.type }))
        return {
          website,
          x,
          ...(discord ? { discord } : {}),
          ...(other.length ? { other } : {}),
        }
      }

      const v1Form = {
        ...formData,
        ...v1Fields,
        nftTransferability: v1Fields.customStartDateTime ? 'custom' : 'never',
      } as DealFormData
      const v1Metadata = isV1Deposit
        ? {
            v1: buildV1DepositMetadata(v1Form, existingMetadata.v1),
            timelines: buildTimelines(v1Form, formData.salesType),
          }
        : {}

      const updatedMetadata = {
        ...existingMetadata,
        ...v1Metadata,
        overview: {
          ...existingOverview,
          projectName: projectName.trim(),
          logoUrl: logoUrl,
          bannerUrl: bannerUrl,
          urls: buildUrlsPayload(urls),
          projectSummary: {
            ...existingProjectSummary,
            projectOverview: description.trim(),
            salesDetails: salesDetails.trim(),
          },
        },
      }

      const payload = {
        ...rawDeal,
        ...(slugChanged ? { slug: slug.trim() } : {}),
        metadata: updatedMetadata,
      }

      await onSubmit(payload)

      showToast({
        message: 'Deal updated successfully',
        type: 'success',
      })

      navigate(`/deals/${dealId}`)
    } catch (error) {
      console.error('Failed to update deal:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to update the deal. Please try again.'

      showToast({
        message: errorMessage,
        type: 'error',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1
            id="v1-edit-heading"
            className="text-3xl font-bold text-neutral-900 mb-2"
          >
            Project Information
          </h1>
          <p className="text-neutral-600">
            Provide basic information about the project.
          </p>
        </div>

        <Card>
          <CardContent className="space-y-6 pt-6">
            <div>
              <label
                htmlFor="project-name"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Project Name
              </label>
              <Input
                id="project-name"
                type="text"
                value={projectName}
                onChange={(e) => {
                  setProjectName(e.target.value)
                  if (errors.projectName) {
                    setErrors((prev) => ({ ...prev, projectName: undefined }))
                  }
                }}
                placeholder="Enter project name"
                className={errors.projectName ? 'border-status-error' : ''}
              />
              {errors.projectName && (
                <p className="text-status-error text-xs mt-2 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.projectName}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="deal-slug"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Deal URL Slug
              </label>
              <Input
                id="deal-slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value)
                  if (errors.slug) {
                    setErrors((prev) => ({ ...prev, slug: undefined }))
                  }
                }}
                placeholder="my-deal"
                className={errors.slug ? 'border-status-error' : ''}
              />
              {errors.slug && (
                <p className="text-status-error text-xs mt-2 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.slug}
                </p>
              )}
              {slugChanged && (
                <p
                  role="alert"
                  className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
                >
                  Changing the slug breaks the old link
                  {formData.slug ? ` (/${formData.slug})` : ''}. Only change it
                  if the link has not been shared yet.
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-8 space-y-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Logo Upload
                </label>
                <FileUpload
                  mode="image"
                  accept="image/png"
                  buttonLabel="Upload PNG"
                  helperText="Upload a square 512×512px PNG"
                  maxSizeMB={1}
                  errorStrings={{
                    fileCount: 'Please upload one image.',
                    fileType: 'PNG only. Please upload a .png file.',
                    fileSize: 'File is too large (max 1 MB).',
                    unreadable:
                      "We couldn't read this image. Please try another file.",
                    generic: 'Upload failed. Please try again.',
                  }}
                  ariaLabel="Upload company logo PNG file"
                  onFileSelect={handleLogoSelected}
                  externalUploading={logoUploading}
                  externalError={logoUploadError}
                  currentFile={logo}
                />
                {logoUploadError && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {logoUploadError}
                  </p>
                )}
              </div>
              <div className="md:col-span-4 space-y-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Live Preview
                </label>
                <LogoPreview logo={logo} fallbackUrl={logoUrl} />
              </div>
            </div>

            <div className="grid md:grid-cols-12 gap-6">
              <div className="md:col-span-8 space-y-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Banner Upload
                </label>
                <FileUpload
                  mode="image"
                  accept="image/png"
                  buttonLabel="Upload Banner"
                  helperText="Upload a 800×192px PNG"
                  maxSizeMB={2}
                  errorStrings={{
                    fileCount: 'Please upload one image.',
                    fileType: 'PNG only. Please upload a .png file.',
                    fileSize: 'File is too large (max 2 MB).',
                    unreadable:
                      "We couldn't read this image. Please try another file.",
                    generic: 'Upload failed. Please try again.',
                  }}
                  ariaLabel="Upload project banner image"
                  onFileSelect={handleBannerSelected}
                  externalUploading={bannerUploading}
                  externalError={bannerUploadError || bannerDimensionError}
                  currentFile={banner}
                  requiredWidth={800}
                  requiredHeight={192}
                />
                {(bannerUploadError || bannerDimensionError) && (
                  <p className="text-status-error text-xs inline-flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" />
                    {bannerUploadError || bannerDimensionError}
                  </p>
                )}
              </div>
              <div className="md:col-span-4 space-y-2">
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Live Preview
                </label>
                <div className="w-full h-24 rounded-lg overflow-hidden bg-neutral-100">
                  {banner && banner instanceof File && banner.size > 0 ? (
                    <img
                      src={URL.createObjectURL(banner)}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  ) : banner &&
                    typeof (banner as any)?.serverUrl === 'string' ? (
                    <img
                      src={(banner as any).serverUrl}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  ) : bannerUrl ? (
                    <img
                      src={bannerUrl}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">
                      No banner uploaded
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-neutral-700">
                URLs
              </label>

              <div className="space-y-3">
                {urls
                  .filter((u) => u.type !== 'discord')
                  .map((urlItem) => {
                    const availableTypes = URL_TYPE_OPTIONS.filter((option) => {
                      if (option.value === 'discord') return false
                      if (!option.singleUse) return true
                      const used = urls.some(
                        (u) => u.id !== urlItem.id && u.type === option.value,
                      )
                      return !used
                    })
                    const selectedType = URL_TYPE_OPTIONS.find(
                      (opt) => opt.value === urlItem.type,
                    )
                    return (
                      <div
                        key={urlItem.id}
                        className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center"
                      >
                        <div className="w-full md:w-40 flex-shrink-0 relative">
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                urlItem.type !== 'website' &&
                                urlItem.type !== 'x'
                              ) {
                                setOpenDropdownId(
                                  openDropdownId === urlItem.id
                                    ? null
                                    : urlItem.id,
                                )
                              }
                            }}
                            disabled={
                              urlItem.type === 'website' || urlItem.type === 'x'
                            }
                            className={cn(
                              'w-full h-10 px-3 py-2 bg-white border border-neutral-200 rounded-md text-sm text-left flex items-center justify-between',
                              'focus-visible:outline-none focus-visible:border-neutral-800',
                              openDropdownId === urlItem.id &&
                                'border-neutral-800',
                              urlItem.type === 'website' || urlItem.type === 'x'
                                ? 'bg-neutral-50 cursor-not-allowed'
                                : '',
                            )}
                            aria-expanded={openDropdownId === urlItem.id}
                            aria-haspopup="listbox"
                          >
                            <span className="text-foreground">
                              {selectedType?.label}
                            </span>
                            {urlItem.type !== 'website' &&
                              urlItem.type !== 'x' && (
                                <ChevronDown
                                  className={cn(
                                    'w-4 h-4 transition-transform text-neutral-600',
                                    openDropdownId === urlItem.id &&
                                      'rotate-180',
                                  )}
                                />
                              )}
                          </button>
                          {openDropdownId === urlItem.id &&
                            urlItem.type !== 'website' &&
                            urlItem.type !== 'x' && (
                              <div className="absolute z-10 w-full mt-1 bg-white text-neutral-950 border border-neutral-200 rounded-md shadow-lg max-h-60 overflow-auto">
                                <div role="listbox">
                                  {availableTypes.map((option) => (
                                    <button
                                      key={option.value}
                                      type="button"
                                      role="option"
                                      aria-selected={
                                        urlItem.type === option.value
                                      }
                                      onClick={() => {
                                        const newType =
                                          option.value as UrlItem['type']
                                        setUrls((prev) =>
                                          prev.map((u) =>
                                            u.id === urlItem.id
                                              ? {
                                                  ...u,
                                                  type: newType,
                                                  url:
                                                    u.type === newType
                                                      ? u.url
                                                      : '',
                                                }
                                              : u,
                                          ),
                                        )
                                        setOpenDropdownId(null)
                                        setErrors((prev) => {
                                          const updated = { ...prev }
                                          delete updated[`url-${urlItem.id}`]
                                          return updated
                                        })
                                      }}
                                      className={cn(
                                        'w-full px-3 py-2 text-left text-sm hover:bg-neutral-50 focus:bg-neutral-50 focus:outline-none',
                                        urlItem.type === option.value &&
                                          'bg-neutral-100 font-medium',
                                      )}
                                    >
                                      {option.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                        </div>

                        <div className="flex-1 min-h-[2.5rem] relative">
                          <Input
                            value={urlItem.url}
                            onChange={(e) => {
                              const value = e.target.value
                              setUrls((prev) =>
                                prev.map((u) =>
                                  u.id === urlItem.id
                                    ? { ...u, url: value }
                                    : u,
                                ),
                              )
                              if (errors[`url-${urlItem.id}`]) {
                                setErrors((prev) => {
                                  const updated = { ...prev }
                                  delete updated[`url-${urlItem.id}`]
                                  return updated
                                })
                              }
                            }}
                            onBlur={() => {
                              const trimmed = urlItem.url.trim()
                              if (!trimmed) {
                                setErrors((prev) => {
                                  const updated = { ...prev }
                                  delete updated[`url-${urlItem.id}`]
                                  return updated
                                })
                                return
                              }
                              const error = getUrlErrorMessage(
                                urlItem.url,
                                urlItem.type,
                              )
                              if (error) {
                                setErrors((prev) => ({
                                  ...prev,
                                  [`url-${urlItem.id}`]: error,
                                }))
                              } else {
                                setErrors((prev) => {
                                  const updated = { ...prev }
                                  delete updated[`url-${urlItem.id}`]
                                  return updated
                                })
                              }
                            }}
                            placeholder={
                              URL_TYPE_OPTIONS.find(
                                (opt) => opt.value === urlItem.type,
                              )?.placeholder || 'https://'
                            }
                            className={cn(
                              'focus:ring-0 placeholder:text-neutral-400',
                              errors[`url-${urlItem.id}`]
                                ? 'border-status-error bg-status-errorBG'
                                : 'focus:border-input',
                            )}
                            aria-label={`URL for ${
                              selectedType?.label ?? 'link'
                            }`}
                            aria-describedby={
                              errors[`url-${urlItem.id}`]
                                ? `url-${urlItem.id}-error`
                                : undefined
                            }
                          />
                          {errors[`url-${urlItem.id}`] && (
                            <p
                              id={`url-${urlItem.id}-error`}
                              className="text-status-error text-xs absolute mt-0 pt-0.5 inline-flex items-center gap-1"
                              role="alert"
                              aria-live="polite"
                            >
                              <AlertCircle className="w-3 h-3" />
                              {errors[`url-${urlItem.id}`]}
                            </p>
                          )}
                        </div>

                        {urlItem.type !== 'website' && urlItem.type !== 'x' && (
                          <div className="flex-shrink-0">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                setUrls((prev) =>
                                  prev.filter((u) => u.id !== urlItem.id),
                                )
                              }
                              className="h-10 w-10 p-0 hover:bg-neutral-100"
                              aria-label="Remove link"
                            >
                              <Trash2 className="w-4 h-4 text-neutral-500" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })}
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setUrls((prev) => [
                    ...prev,
                    { id: `url-${Date.now()}`, type: 'custom', url: '' },
                  ])
                }
                disabled={URL_TYPE_OPTIONS.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add URL
              </Button>
            </div>

            <div>
              <label
                htmlFor="project-overview"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Project Narrative
              </label>
              <div
                className={`rounded-lg ${
                  errors.description
                    ? 'border border-status-error bg-red-50/40'
                    : 'border border-transparent'
                }`}
              >
                <RichTextEditor
                  value={description}
                  onChange={(value: string) => {
                    setDescription(value)
                    if (errors.description) {
                      setErrors((prev) => ({ ...prev, description: undefined }))
                    }
                  }}
                  placeholder="Recomended structure: Project Overview, Traction, Token Utility & Economics, Roadmap, Competitive Landscape, Team, Investment Thesis, Risk Evaluation, Project Coverage."
                  name="project-overview"
                  minHeight={320}
                />
              </div>
              {errors.description && (
                <p className="text-status-error text-xs mt-2 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.description}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="sales-overview"
                className="block text-sm font-medium text-neutral-700 mb-2"
              >
                Sale Details
              </label>
              <div
                className={`rounded-lg ${
                  errors.salesDetails
                    ? 'border border-status-error bg-red-50/40'
                    : 'border border-transparent'
                }`}
              >
                <RichTextEditor
                  value={salesDetails}
                  onChange={(value: string) => {
                    setSalesDetails(value)
                    if (errors.salesDetails) {
                      setErrors((prev) => ({
                        ...prev,
                        salesDetails: undefined,
                      }))
                    }
                  }}
                  placeholder="Describe the sales."
                  name="sales-overview"
                  minHeight={320}
                />
              </div>
              {errors.salesDetails && (
                <p className="text-status-error text-xs mt-2 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.salesDetails}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {isV1Deposit && (
          <div className="mt-6">
            <V1DepositFields
              data={v1Fields}
              errors={errors}
              onChange={updateV1Field}
              onPatch={updateV1Lists}
              locked
              lockedConfigCount={lockedConfigCount}
              tvl={isTvl}
              addressesEditable={addressesEditable}
            />
          </div>
        )}

        <div className="flex justify-end pt-6">
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || isLoading}
            variant="brand"
            size="md"
            className="flex items-center gap-2 px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
